/* =====================================================================================
   Templates: the org's stationery, in one place, at one version.

   The files everything else is built on top of, headed paper and a slide template, normally live
   in somebody's Downloads folder in three slightly different versions, and the one that reaches a
   customer is whichever the sender happened to have. That is a branding problem for a deck and a
   Companies Act problem for a letter, which must state the registered name, number, place of
   registration and registered office.

   TENANT AGNOSTIC. A template is a named file with a kind, a version and a note on how to use it.
   Nothing here knows what the tenant sells.

   THE BUCKET IS PRIVATE AND THE LINK IS SIGNED AND SHORT-LIVED. Blank headed paper is precisely
   the asset that should not sit on a public URL: anyone who found it could produce convincing
   correspondence on the company's letterhead.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import './templates.css';

interface Template {
  id: string;
  key: string;
  name: string;
  kind: string;
  description: string | null;
  guidance: string | null;
  file_path: string | null;
  bundle_prefix: string | null;
  mime: string | null;
  file_size: number | null;
  version: string;
  updated_at: string;
}

const KIND_LABEL: Record<string, string> = {
  letterhead: 'Headed paper',
  presentation: 'Slides',
  document: 'Document',
  spreadsheet: 'Spreadsheet',
  email: 'Email',
  report: 'Report',
  label: 'Label',
  other: 'Other',
};

const size = (b: number | null) =>
  b == null ? '' : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

/* A bundle row (an archive with a storage folder beside it) opens into its own file tree,
   so one logo or one icon can be fetched without pulling the whole archive down. Folders
   list lazily on first open; every file link is signed for two minutes, like the main
   download. Storage returns folders as entries with a null id. */
interface Entry { name: string; id: string | null; metadata: { size?: number } | null }

function BundleFolder({ prefix, depth }: { prefix: string; depth: number }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => { (async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().storage.from('templates')
        .list(prefix, { limit: 500, sortBy: { column: 'name', order: 'asc' } });
      if (error) throw new Error(error.message);
      setEntries((data ?? []) as Entry[]);
    } catch (e) { setErr(String((e as Error).message)); }
  })(); }, [prefix]);

  async function fetchOne(name: string) {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().storage.from('templates')
        .createSignedUrl(`${prefix}/${name}`, 120, { download: true });
      if (error) throw new Error(error.message);
      window.location.href = data.signedUrl;
    } catch (e) { setErr(String((e as Error).message)); }
  }

  if (err) return <p className="tpl-tree-err">{err}</p>;
  if (!entries) return <p className="muted tpl-tree-loading">Listing&hellip;</p>;
  const folders = entries.filter((e) => e.id === null);
  const files = entries.filter((e) => e.id !== null && e.name !== '.emptyFolderPlaceholder');
  return (
    <ul className="tpl-tree" style={{ ['--tpl-depth' as string]: depth }}>
      {folders.map((f) => (
        <li key={f.name}>
          <button className="tpl-tree-folder"
                  onClick={() => setOpen((o) => ({ ...o, [f.name]: !o[f.name] }))}>
            <span className="tpl-caret">{open[f.name] ? '\u25be' : '\u25b8'}</span> {f.name}
          </button>
          {open[f.name] && <BundleFolder prefix={`${prefix}/${f.name}`} depth={depth + 1} />}
        </li>
      ))}
      {files.map((f) => (
        <li key={f.name} className="tpl-tree-file">
          <span className="tpl-tree-name">{f.name}</span>
          <span className="tpl-meta">{size(f.metadata?.size ?? null)}</span>
          <button className="link-btn" onClick={() => fetchOne(f.name)}>Download</button>
        </li>
      ))}
      {folders.length === 0 && files.length === 0 && <li className="muted">Empty.</li>}
    </ul>
  );
}

export function TemplatesView() {
  const [rows, setRows] = useState<Template[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (BACKEND !== 'supabase') { setRows([]); return; }
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().schema('studio').from('templates')
        .select('id, key, name, kind, description, guidance, file_path, bundle_prefix, mime, file_size, version, updated_at')
        .eq('is_current', true).order('kind').order('name');
      if (error) throw new Error(error.message);
      setRows((data ?? []) as Template[]);
    } catch (e) { setErr(String((e as Error).message)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function download(t: Template) {
    if (!t.file_path) return;
    setBusy(t.id);
    try {
      const { supabase } = await import('../lib/supabase');
      /* Signed for two minutes. Long enough to click, short enough that a link pasted into a
         chat has expired by the time anyone else opens it. */
      const { data, error } = await supabase().storage.from('templates')
        .createSignedUrl(t.file_path, 120, { download: true });
      if (error) throw new Error(error.message);
      window.location.href = data.signedUrl;
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  async function upload(t: Template, file: File) {
    setBusy(t.id); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: org } = await supabase().schema('app').rpc('current_org_id');
      /* Foldered by org, because the storage policies key on the first path segment. Getting
         this wrong uploads successfully and then cannot be read back by anyone, including the
         person who uploaded it. */
      const path = `${org}/${t.key}-${t.version}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
      const { error: upErr } = await supabase().storage.from('templates')
        .upload(path, file, { upsert: true, contentType: file.type || undefined });
      if (upErr) throw new Error(upErr.message);

      const { error: rowErr } = await supabase().schema('studio').from('templates')
        .update({ file_path: path, mime: file.type, file_size: file.size })
        .eq('id', t.id);
      if (rowErr) throw new Error(`Uploaded, but not recorded: ${rowErr.message}`);

      setMsg(`${t.name} updated.`);
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  if (err) return <p className="block-error" role="alert">{err}</p>;
  if (!rows) return <p className="muted">Loading</p>;

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Templates</h1>
        <p className="page-sub">
          {TENANT.brand.orgName}&rsquo;s stationery, held once so what leaves the building is the
          same file every time.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {rows.length === 0 && (
        <p className="muted">
          No templates registered yet.
        </p>
      )}

      <div className="tpl-grid">
        {rows.map((t) => (
          <section key={t.id} className="card tpl">
            <div className="tpl-head">
              <span className="tpl-kind">{KIND_LABEL[t.kind] ?? t.kind}</span>
              <span className="tpl-version">{t.version}</span>
            </div>
            <h2 className="tpl-name">{t.name}</h2>
            {t.description && <p className="tpl-desc">{t.description}</p>}

            {t.guidance && (
              <p className="tpl-guidance"><strong>Before you use it.</strong> {t.guidance}</p>
            )}

            <div className="tpl-foot">
              {t.file_path ? (
                <>
                  <button className="btn primary" disabled={busy === t.id}
                          onClick={() => download(t)}>
                    {busy === t.id ? 'Preparing' : 'Download'}
                  </button>
                  <span className="tpl-meta">{size(t.file_size)}</span>
                </>
              ) : (
                /* Said out loud rather than shown as an empty card. A register that quietly
                   omits what it does not have is how somebody concludes there is no letterhead
                   and makes their own. */
                <span className="tpl-missing">Not uploaded yet</span>
              )}

              <label className="btn tpl-upload">
                {t.file_path ? 'Replace' : 'Upload'}
                <input type="file" hidden
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         if (f) upload(t, f);
                         e.target.value = '';
                       }} />
              </label>
              {t.bundle_prefix && (
                <button className="btn" onClick={() =>
                  setBrowsing((b) => ({ ...b, [t.id]: !b[t.id] }))}>
                  {browsing[t.id] ? 'Hide the files' : 'Browse the files'}
                </button>
              )}
            </div>
            {t.bundle_prefix && browsing[t.id] && (
              <div className="tpl-browse">
                <BundleFolder prefix={t.bundle_prefix} depth={0} />
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
